import { useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import {
  PromotionScheduleStatus,
  PromotionScheduleTrack,
  PromotionSchedulesFindQuery,
} from '../../../../lib/api/promotionSchedule';
import { PERIOD_UNIT, PeriodUnit, VIEW_MODE, ViewMode } from '../constants';
import { DATE_FORMAT, getDefaultRange, toQueryRange } from '../utils';

type UseTimelineFilterReturn = {
  periodUnit: PeriodUnit;
  range: [Dayjs, Dayjs];
  tracks: PromotionScheduleTrack[];
  statuses: PromotionScheduleStatus[];
  adminIds: number[];
  viewMode: ViewMode;
  /** 서버 조회 파라미터. 빈 배열 필터는 "전체"라 아예 보내지 않는다. */
  query: PromotionSchedulesFindQuery;
  setPeriodUnit: (periodUnit: PeriodUnit) => void;
  setRange: (range: [Dayjs, Dayjs]) => void;
  setTracks: (tracks: PromotionScheduleTrack[]) => void;
  setStatuses: (statuses: PromotionScheduleStatus[]) => void;
  setAdminIds: (adminIds: number[]) => void;
  setViewMode: (viewMode: ViewMode) => void;
};

/**
 * 간트/캘린더가 공유하는 필터 상태.
 *
 * 뷰를 메뉴로 나누지 않고 한 화면 안에서 토글하는 이유가 이것 —
 * 전환할 때 기간·트랙 선택이 유지돼야 한다.
 */
export const useTimelineFilter = (): UseTimelineFilterReturn => {
  const [periodUnit, setPeriodUnitState] = useState<PeriodUnit>(PERIOD_UNIT.MONTH);
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => getDefaultRange(PERIOD_UNIT.MONTH));
  const [tracks, setTracks] = useState<PromotionScheduleTrack[]>([]);
  const [statuses, setStatuses] = useState<PromotionScheduleStatus[]>([]);
  const [adminIds, setAdminIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_MODE.GANTT);

  /** 단위를 바꾸면 기간도 그 단위의 기본값으로 되돌린다 (일 단위 범위를 연 단위로 그대로 쓰면 어색하다) */
  const setPeriodUnit = (nextUnit: PeriodUnit) => {
    setPeriodUnitState(nextUnit);
    setRange(getDefaultRange(nextUnit));
  };

  const query = useMemo<PromotionSchedulesFindQuery>(() => {
    // 월/연 단위 선택은 경계를 채워서 보낸다 (월=1일~말일, 연=1/1~12/31)
    const [from, to] = toQueryRange(range, periodUnit);

    return {
      from: from.format(DATE_FORMAT),
      to: to.format(DATE_FORMAT),
      ...(tracks.length > 0 ? { tracks } : {}),
      ...(statuses.length > 0 ? { statuses } : {}),
      ...(adminIds.length > 0 ? { adminIds } : {}),
    };
  }, [range, periodUnit, tracks, statuses, adminIds]);

  return {
    periodUnit,
    range,
    tracks,
    statuses,
    adminIds,
    viewMode,
    query,
    setPeriodUnit,
    setRange,
    setTracks,
    setStatuses,
    setAdminIds,
    setViewMode,
  };
};
